import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/session';
import { closePass, createTemplate, getPass, getPassAccess, getPassEvents, type ObjectInfo } from '../../api/tenant';
import { canEditByReplace } from '../../domain/passEdit';
import { passPageLink, passReadyMessage, safeInviteLink, inviteMessage } from '../../domain/invite';
import { describePassError } from '../../domain/passErrors';
import { describeEvent, wasUsed } from '../../domain/passEvents';
import { GuestType, PassStatus, parseServerDate } from '../../domain/passes';
import { passActions } from '../../domain/passTabs';
import type { PassDuration } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';
import { Plate, WalkerIcon } from '../../ui/Plate';
import { Skeleton } from '../../ui/Skeleton';
import { StatusChip } from '../../ui/StatusChip';
import { LoadFailed } from '../common/LoadFailed';
import { passLabel } from '../common/PassRow';
import { windowCaption } from '../common/windowCaption';
import f from '../pass-form/passForm.module.css';
import s from './passCard.module.css';

const DURATION: Record<number, PassDuration> = { 1: 'oneTime', 2: 'temporary', 3: 'permanent' };

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
}

/** Pass card (spec §6.6): header, guest access, history, actions by state. */
export function PassCardScreen({ session, telegram, object }: Props) {
  const id = Number(useParams().id);
  const queryClient = useQueryClient();
  const pass = useQuery({ queryKey: ['pass', id], queryFn: () => getPass(session, id) });
  const events = useQuery({ queryKey: ['pass', id, 'events'], queryFn: () => getPassEvents(session, id) });
  const p = pass.data;
  const hasAccess = !!p?.sharedToken && (p.status === PassStatus.Expected || p.status === PassStatus.Inside);
  const access = useQuery({ queryKey: ['pass', id, 'access'], queryFn: () => getPassAccess(session, p!.sharedToken!), enabled: hasAccess });

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const close = useMutation({
    mutationFn: () => closePass(session, id),
    onSuccess: (updated) => {
      telegram.notify('success');
      queryClient.setQueryData(['pass', id], (old: typeof p) => (old ? { ...old, ...updated } : updated));
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      void queryClient.invalidateQueries({ queryKey: ['pass', id, 'events'] });
      setConfirming(false);
    },
    onError: (e) => {
      telegram.notify('error');
      setConfirming(false);
      setError(describePassError(e).general);
      if (e instanceof ApiError && e.code === 'CLOSED_PASS') void pass.refetch();
    },
  });

  const save = useMutation({
    mutationFn: () =>
      createTemplate(session, {
        name: passLabel(p!),
        addressId: p!.address.id,
        durationType: p!.durationType,
        guestType: p!.guestType ?? GuestType.Vehicle,
        guestData: p!.guestType === GuestType.Pedestrian ? { name: p!.guestData?.name } : { plateNumber: p!.guestData?.plateNumber },
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e) => setError(describePassError(e).general),
  });

  if (pass.isError) return <LoadFailed error={pass.error} onRetry={() => void pass.refetch()} fullScreen />;
  if (!p) {
    return (
      <main className={s.card} aria-busy="true">
        <Skeleton height={120} />
        <Skeleton height={200} />
      </main>
    );
  }

  const used = wasUsed(events.data ?? []);
  const actions = passActions(p.status, used);
  const now = new Date();
  const duration = DURATION[p.durationType] ?? 'oneTime';
  const shareTarget =
    p.status === PassStatus.WaitingGuest ? safeInviteLink(p.inviteLink) : passPageLink(p.sharedToken);
  const repeatParam =
    p.guestType === GuestType.Pedestrian && p.guestData?.name ? `pedestrian:${p.guestData.name}`
    : p.guestData?.plateNumber ? `vehicle:${p.guestData.plateNumber}`
    : null;
  const osmi = access.data?.linkOsmi;
  const isInvite = p.status === PassStatus.WaitingGuest;

  return (
    <main className={s.card}>
      <header className={s.head}>
        <div className={s.headRow}>
          {p.guestData?.plateNumber ? <Plate value={p.guestData.plateNumber} /> : <WalkerIcon />}
          <StatusChip status={p.status} />
        </div>
        <h1 className={s.title}>{passLabel(p)}</h1>
        <p className={s.meta}>{p.address.name}</p>
        <p className={s.meta}>{windowCaption(duration, parseServerDate(p.startsAt), parseServerDate(p.expiresAt), now, 'since')}</p>
        {p.comment && <p className={s.comment}>{p.comment}</p>}
      </header>

      {hasAccess && (
        <section className={s.section} aria-label="Доступ для гостя">
          {access.isPending ? (
            <Skeleton height={160} />
          ) : access.data ? (
            <div className={s.access}>
              {/* qrImage encodes the entry code. sharedTokenQr is never used: it carries chs=1. */}
              {access.data.qrImage?.startsWith('data:image/') && <img className={s.qr} src={access.data.qrImage} alt="QR-код для прохода" />}
              {access.data.pin && (
                <p className={s.pin}>
                  PIN <strong>{access.data.pin}</strong>
                </p>
              )}
              {access.data.accessCode && (
                <p className={s.pin}>
                  Код доступа <strong>{access.data.accessCode}</strong>
                </p>
              )}
              {(osmi?.linkIos || osmi?.linkAndroid) && (
                <div className={s.wallet}>
                  {osmi.linkIos && (
                    <a href={osmi.linkIos} className={s.walletLink} onClick={(e) => { e.preventDefault(); telegram.openLink(osmi.linkIos!); }}>
                      В Apple Wallet
                    </a>
                  )}
                  {osmi.linkAndroid && (
                    <a href={osmi.linkAndroid} className={s.walletLink} onClick={(e) => { e.preventDefault(); telegram.openLink(osmi.linkAndroid!); }}>
                      В Google Wallet
                    </a>
                  )}
                </div>
              )}
              {!access.data.qrImage && !access.data.pin && !access.data.accessCode && (
                <p className={s.meta}>Гость проедет по номеру или имени — отдельный код не нужен.</p>
              )}
            </div>
          ) : null}
        </section>
      )}

      <div className={s.actions}>
        {shareTarget && (
          <button type="button" className={f.submit} onClick={() => telegram.share(shareTarget, isInvite ? inviteMessage(object.name) : passReadyMessage(object.name))}>
            Отправить гостю
          </button>
        )}
        {canEditByReplace(p.status) && p.durationType === 2 && (
          <Link to={`/new?edit=${p.id}&extend=7`} className={s.ghost}>
            Продлить на неделю
          </Link>
        )}
        {canEditByReplace(p.status) && (
          <Link to={`/new?edit=${p.id}`} className={s.ghost}>
            Изменить
          </Link>
        )}
        {actions.repeat && repeatParam && (
          <Link to={`/new?guest=${encodeURIComponent(repeatParam)}`} className={s.ghost}>
            Повторить
          </Link>
        )}
        {actions.save && repeatParam && (
          <button type="button" className={s.ghost} disabled={saved || save.isPending} onClick={() => save.mutate()}>
            {saved ? 'Сохранено в частых гостях' : 'В частые гости'}
          </button>
        )}
        {actions.close === 'blockedInside' && <p className={s.note}>Закрыть можно после выезда гостя</p>}
        {(actions.close === 'close' || actions.close === 'cancelInvite') &&
          (confirming ? (
            <div className={s.confirm} role="group" aria-label="Подтверждение">
              <p className={s.note}>{isInvite ? 'Ссылка перестанет работать.' : 'Гость больше не сможет проехать по этому пропуску.'}</p>
              <button type="button" className={s.danger} onClick={() => close.mutate()} disabled={close.isPending}>
                {isInvite ? 'Да, отменить' : 'Да, закрыть'}
              </button>
              <button type="button" className={s.ghost} onClick={() => setConfirming(false)}>
                Нет
              </button>
            </div>
          ) : (
            <button type="button" className={s.dangerLink} onClick={() => { setError(null); setConfirming(true); }}>
              {isInvite ? 'Отменить приглашение' : 'Закрыть пропуск'}
            </button>
          ))}
        {error && (
          <p className={f.error} role="alert">
            {error}
          </p>
        )}
      </div>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>История</h2>
        {events.isPending ? (
          <Skeleton height={80} />
        ) : events.isError ? (
          <LoadFailed error={events.error} onRetry={() => void events.refetch()} />
        ) : (
          <ol className={s.events}>
            {(events.data ?? []).map((e, i) => {
              const d = describeEvent(e);
              return (
                <li key={`${e.happenedAt}-${i}`} className={s.event}>
                  <span className={s.eventTime}>{d.time}</span>
                  <span className={s.eventText}>{d.text}</span>
                  {d.who && <span className={s.eventWho}>{d.who}</span>}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </main>
  );
}
